import {
  Box,
  CircularProgress,
  Paper,
  Divider,
  Container,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
// import Logo from "Assets/svgs/logo.svg"; // replace path as needed
import Logo from "Assets/images/logo.png";

import { Typography,  Button } from "Atoms";
import TestRequirements from "./_tabs/TestRequirements";
import { useBoundStore } from "Zustand/store/mainStore";
import { useLocation } from "react-router";
import { ArrowBack, CopyAll, Logout } from "@mui/icons-material";
import CreateEditEntityModal from "Pages/Projects/CreateEditEntityModal";
import {
  addSpecificationToProjectAPI,
  deleteTestProjectAPI,
  getCustomerListAPI,
  getTestProjectByNodeIdAPI,
  getTestSpecificationsAPI,
  getTestPlansAPI,
  updateTestProjectAPI,
} from "Zustand/services/projectsService";
import { getAttachmentChanges } from "Utils/helperFunctions";
import { deleteAttachmentsAPI } from "Zustand/services/attachmentService";
import DeletePopUp from "Components/DeletePopUp";
import { useNavigate } from "react-router-dom";
import BasicInfo from "./_tabs/BasicInfo";
import Attachments from "./_tabs/Attachments";
import Appbar from "Atoms/Appbar";
import TestFlow from "./_tabs/TestFlow";
import { toast } from "react-toastify";

interface Attachment {
  id: string;
  title: string;
  description: string;
  file?: File;
  url?: string;
}

type CustomerOption = {
  label: string;
  value: string;
};

const normalizeCustomerOptions = (response: any): CustomerOption[] => {
  const source =
    (Array.isArray(response) && response) ||
    response?.customers ||
    response?.customerlist ||
    response?.customerList ||
    response?.data ||
    [];

  if (!Array.isArray(source)) return [];

  return source
    .map((item: any) => {
      const value =
        typeof item === "string"
          ? item
          : item?.customer ||
            item?.customerName ||
            item?.customername ||
            item?.name ||
            item?.id ||
            "";

      const label =
        typeof item === "string"
          ? item
          : item?.customerName ||
            item?.customername ||
            item?.name ||
            item?.customer ||
            value;

      return value ? { label: String(label), value: String(value) } : null;
    })
    .filter(Boolean) as CustomerOption[];
};

// Project detail can arrive either as `{ testproject: ... }`, a legal-entity
// wrapper, or the raw testproject object. Keep one stable shape for the form.
const normalizeProjectDetail = (response: any) => {
  if (!response) return response;

  if (Array.isArray(response)) {
    return normalizeProjectDetail(response[0]);
  }

  if (response?.testproject) return response;

  const projectFromLegalEntity =
    response?.legalentity?.hastestproject?.[0] ||
    response?.[0]?.legalentity?.hastestproject?.[0];

  if (projectFromLegalEntity?.testproject) return projectFromLegalEntity;

  return { testproject: response };
};

const ProjectDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = (location.state || {}) as any;
  const { projectnodeid, plannodeid, project } = routeState;
  const [tabIndex, setTabIndex] = useState(routeState?.initialTabIndex ?? 0);

  const {
    logout,
    getTestPlanContent,
    setIsUploading,
    uploadAttachments,
    isUploading,
    legalEntityNodeid,
    // project,
    // getProjectByNodeId,
  } = useBoundStore();

  const [projectDetail, setProjectDetail] = useState<any>(
    normalizeProjectDetail(project)
  );

  const [open, setOpen] = useState(false); // ← modal flag

  const [originalAttachments, setOriginalAttachments] = useState<Attachment[]>(
    []
  );
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [testSpecifications, setTestSpecifications] = useState<any[]>([]);
  const [selectedTestSpecification, setSelectedTestSpecification] =
    useState("");
  const [creatingTestPlan, setCreatingTestPlan] = useState(false);
  const [projectTestPlans, setProjectTestPlans] = useState<any[]>([]);
  const [loadingProjectTestPlans, setLoadingProjectTestPlans] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const currentProjectCustomer =
    projectDetail?.testproject?.customer || projectDetail?.customer || "";
  const customerOptionsWithCurrent = useMemo(() => {
    if (
      !currentProjectCustomer ||
      customerOptions.some((option) => option.value === currentProjectCustomer)
    ) {
      return customerOptions;
    }

    return [
      { label: currentProjectCustomer, value: currentProjectCustomer },
      ...customerOptions,
    ];
  }, [currentProjectCustomer, customerOptions]);

  const handleTabChange = (_: any, newValue: number) => {
    setTabIndex(newValue);
  };

  useEffect(() => {
    if (typeof routeState?.initialTabIndex === "number") {
      setTabIndex(routeState.initialTabIndex);
    }
  }, [routeState?.initialTabIndex]);

  const handleUpdateProject = async ({
    projectName,
    projectDesc,
    customer,
  }: {
    projectName: string;
    projectDesc: string;
    attachments: any[];
    customer?: string;
  }) => {
    try {
      if (
        projectName !== projectDetail?.testproject?.name ||
        projectDesc !== projectDetail?.testproject?.description ||
        customer !== currentProjectCustomer
      ) {
        await updateTestProjectAPI({
          nodeid: projectnodeid,
          lang: "en",
          name: projectName,
          description: projectDesc,
          customer: customer || "",
        });
      }

      /* 2️⃣  diff + handle attachments exactly as before … */
      const { toAdd, toDelete, toEdit } = getAttachmentChanges(
        originalAttachments,
        attachments
      ) as any;

      // Handle attachments

      if (toAdd.length > 0) {
        setIsUploading(true);
        await uploadAttachments(projectnodeid, [...toAdd], []);
        setIsUploading(false);
      }

      if (toDelete.length > 0) {
        setIsUploading(true);
        await deleteAttachmentsAPI(toDelete.map((item: any) => item.nodeid));
        setIsUploading(false);
      }
      if (toEdit.length > 0) {
        setIsUploading(true);
        await uploadAttachments(projectnodeid, [], [...toEdit]);
        setIsUploading(false);
      }

      navigate("/projects");
      setOpen(false);
    } catch (err) {
      console.error("Update project failed", err);
      // toast / snackbar if desired
    }
  };

  const normalizeApiAttachments = (apiData: any[]): Attachment[] => {
    return apiData.map((item) => ({
      id: item.attachment.file.fileid,
      title: item.attachment.title,
      description: item.attachment.description,
      url: item.attachment.file.url,
      nodeid: item.attachment.nodeid,
    }));
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (plannodeid) {
      getTestPlanContent(plannodeid);
    }
  }, [plannodeid]);

  const handleDeleteDialogToggle = () => {
    setOpenDialogDelete(!openDialogDelete);
  };

  const handleDelete = async () => {
    await deleteTestProjectAPI(projectnodeid);
    navigate("/projects");
  };

  const loadProjectTestPlans = async () => {
    if (!projectnodeid || routeState.hasPlan) {
      setProjectTestPlans([]);
      return;
    }

    setLoadingProjectTestPlans(true);
    try {
      const plans = await getTestPlansAPI(projectnodeid);
      setProjectTestPlans(plans || []);
    } catch (error) {
      console.error("Failed to load test plans", error);
      setProjectTestPlans([]);
    } finally {
      setLoadingProjectTestPlans(false);
    }
  };

  const loadProjectDetail = async () => {
    if (!projectnodeid) return null;

    try {
      const latestProject = await getTestProjectByNodeIdAPI(projectnodeid);
      const normalizedProject = normalizeProjectDetail(latestProject);
      setProjectDetail(normalizedProject);
      return normalizedProject;
    } catch (error) {
      console.error("Failed to refresh project detail", error);
      return null;
    }
  };

  const loadCustomerOptions = async () => {
    setIsLoadingCustomers(true);
    try {
      const response = await getCustomerListAPI();
      setCustomerOptions(normalizeCustomerOptions(response));
    } catch (error) {
      console.error("Failed to load customers", error);
      setCustomerOptions([]);
      toast.error("Failed to load customers");
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  const handleOpenTestPlan = (testPlanNodeId: string) => {
    navigate("/project-detail", {
      state: {
        projectnodeid,
        project: projectDetail,
        plannodeid: testPlanNodeId,
        hasPlan: true,
      },
    });
  };

  const handleCreateTestPlan = async () => {
    if (!selectedTestSpecification) {
      toast.error("Please select a test specification");
      return;
    }

    setCreatingTestPlan(true);
    try {
      const payload = {
        testspecificationnodeid: selectedTestSpecification,
      };

      await addSpecificationToProjectAPI(projectnodeid, payload);
      await Promise.all([loadProjectDetail(), loadProjectTestPlans()]);
      setSelectedTestSpecification("");
      setTabIndex(1);
      toast.success("Test plan created");
    } catch (error) {
      console.error("Failed to create test plan from test specification", error);
      toast.error("Failed to create test plan");
    } finally {
      setCreatingTestPlan(false);
    }
  };

  // useEffect(() => {
  //   if (projectnodeid) getProjectByNodeId(projectnodeid);
  // }, [projectnodeid]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    // if user selects any requirement from tree then mapping data to form
    setAttachments([]);
    if (projectDetail) {
      const normalized = normalizeApiAttachments(
        projectDetail?.testproject?.hasattachment || []
      );
      setOriginalAttachments(normalized);
      setAttachments(normalized); // passed to AttachmentManager
    }
  }, [projectDetail]);

  useEffect(() => {
    if (project) {
      setProjectDetail(normalizeProjectDetail(project));
    }
  }, [project]);

  useEffect(() => {
    let active = true;

    const loadTestSpecifications = async () => {
      if (!legalEntityNodeid || routeState.hasPlan) {
        setTestSpecifications([]);
        return;
      }

      try {
        const specifications = await getTestSpecificationsAPI(legalEntityNodeid);
        if (!active) return;
        setTestSpecifications(specifications);
      } catch (error) {
        console.error("Failed to load test specifications", error);
        if (!active) return;
        setTestSpecifications([]);
      }
    };

    loadTestSpecifications();

    return () => {
      active = false;
    };
  }, [legalEntityNodeid, routeState.hasPlan]);

  useEffect(() => {
    loadProjectTestPlans();
  }, [projectnodeid, routeState.hasPlan]);

  useEffect(() => {
    if (!routeState.hasPlan) {
      loadCustomerOptions();
      loadProjectDetail();
    }
  }, [routeState.hasPlan, projectnodeid]);

  return (
    <Container
      maxWidth="xl"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <DeletePopUp
        open={openDialogDelete}
        handleClose={handleDeleteDialogToggle}
        handleDelete={handleDelete}
      ></DeletePopUp>

      <Paper
        sx={{ width: "100%", p: 4, borderRadius: 2, height: "100%" }}
        elevation={3}
      >
        {/* 🔷 Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="text"
              onClick={() => {
                window.history.back();
              }}
              startIcon={<ArrowBack />}
            >
              Back
            </Button>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            gap={1}
            flexDirection={"column"}
          >
            <img src={Logo} alt="Logo" style={{ height: 40 }} />
          </Box>
          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={logout}
            startIcon={<Logout />}
          >
            Logout
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box display="flex" justifyContent="center" alignItems="center" mb={1}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "25px",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <b>Project : </b> {projectDetail?.testproject?.name}{" "}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <b>Plan : </b>
              <Tooltip title={plannodeid || ""}>
                <Typography variant="body2" component={"span"}>
                  {plannodeid
                    ? `${plannodeid.substring(0, 8)}...${plannodeid.substring(
                        plannodeid.length - 5
                      )}`
                    : "—"}{" "}
                </Typography>
              </Tooltip>
              <Tooltip title="Copy full Plan ID">
                <IconButton
                  onClick={() => {
                    navigator.clipboard.writeText(plannodeid || "");
                  }}
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                >
                  <CopyAll fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {routeState.hasPlan ? (
          <>
            {/* 🔷 Tabs */}
  
            <Appbar
              tabs={[
                { label: "Basic Info", value: 0 },
                { label: "Test Requirements", value: 1 },
                { label: "Test Flow", value: 2 },
                { label: "Test Plans", value: 3 },
              ]}
              value={tabIndex}
              onChange={handleTabChange}
              primaryColor="primary" // appbar bg
              textColor="#FFFFFF" // tab text
              activeBarColor="#FFFFFF" // underline/indicator
            />

            <Box mt={2}>
              {tabIndex === 0 && (
                <BasicInfo
                  project={projectDetail}
                  projectnodeid={projectDetail?.testproject?.nodeid || projectnodeid}
                  testplannodeid={location?.state?.plannodeid}
                />
              )}
              {tabIndex === 1 && <TestRequirements />}
              {tabIndex === 2 && (
                <TestFlow testPlanNodeId={location?.state?.plannodeid} />
              )}
              {tabIndex === 3 && (
                <Attachments testPlanNodeId={location?.state?.plannodeid} />
              )}
            </Box>
          </>
        ) : (
          <Box>
            <Typography variant="h5" sx={{ mb: 2 }}>
              <b>Project Detail</b>
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Appbar
              tabs={[
                { label: "Basic Info", value: 0 },
                { label: "Test Plans", value: 1 },
              ]}
              value={tabIndex}
              onChange={handleTabChange}
              primaryColor="primary"
              textColor="#FFFFFF"
              activeBarColor="#FFFFFF"
            />

            <Box mt={2}>
              {tabIndex === 0 && (
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                  <CreateEditEntityModal
                    isEditMode
                    open={open}
                    onClose={() => setOpen(false)}
                    onSave={handleUpdateProject}
                    initialName={projectDetail?.testproject?.name}
                    initialDesc={projectDetail?.testproject?.description}
                    initialCustomer={currentProjectCustomer}
                    disableSaveUntilChanged
                    disableFileChangeForExisting
                    attachments={attachments}
                    setAttachments={setAttachments}
                    isUploading={isUploading}
                    isModel={false}
                    showCustomerSelect
                    customerOptions={customerOptionsWithCurrent}
                    customerRequired
                    isLoadingCustomers={isLoadingCustomers}
                    extraContent={
                      <>
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Select Test Specification</InputLabel>
                          <MuiSelect
                            label="Select Test Specification"
                            value={selectedTestSpecification}
                            onChange={(event) =>
                              setSelectedTestSpecification(event.target.value)
                            }
                          >
                            {testSpecifications.map((specification: any) => {
                              const value =
                                specification?.testspecification?.nodeid ||
                                specification?.nodeid ||
                                "";
                              const label =
                                specification?.testspecification?.name ||
                                specification?.name ||
                                specification?.testspecification?.nodeid ||
                                "Unnamed Test Specification";

                              return (
                                <MenuItem key={value} value={value}>
                                  {label}
                                </MenuItem>
                              );
                            })}
                          </MuiSelect>
                        </FormControl>

                        <Box display="flex" justifyContent="flex-start">
                          <Button
                            variant="outlined"
                            onClick={handleCreateTestPlan}
                            disabled={
                              !selectedTestSpecification || creatingTestPlan
                            }
                          >
                            {creatingTestPlan ? (
                              <CircularProgress
                                size={20}
                                color="inherit"
                                sx={{ mr: 1 }}
                              />
                            ) : null}
                            Create Testplan
                          </Button>
                        </Box>
                      </>
                    }
                  />
                </Paper>
              )}

              {tabIndex === 1 && (
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Phase</TableCell>
                          <TableCell>Version</TableCell>
                          <TableCell>Plan ID</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loadingProjectTestPlans ? (
                          <TableRow>
                            <TableCell colSpan={4}>
                              <Box
                                py={4}
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                              >
                                <CircularProgress size={24} />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : projectTestPlans.length > 0 ? (
                          projectTestPlans.map((planWrap: any) => {
                            const plan = planWrap?.testplan || planWrap;
                            return (
                              <TableRow key={plan?.nodeid || plan?.id} hover>
                                <TableCell>{plan?.phase || "—"}</TableCell>
                                <TableCell>{plan?.version || "—"}</TableCell>
                                <TableCell>{plan?.nodeid || "—"}</TableCell>
                                <TableCell align="right">
                                  <Button
                                    variant="contained"
                                    onClick={() =>
                                      handleOpenTestPlan(plan?.nodeid || "")
                                    }
                                    disabled={!plan?.nodeid}
                                  >
                                    Open
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4}>
                              <Box py={4} textAlign="center" color="text.secondary">
                                No test plans found
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </Box>
          </Box>
        )}
      </Paper>

      <CreateEditEntityModal
        isEditMode
        open={open}
        onClose={() => setOpen(false)}
        onSave={handleUpdateProject}
        initialName={projectDetail?.testproject?.name}
        initialDesc={projectDetail?.testproject?.description}
        initialCustomer={currentProjectCustomer}
        disableSaveUntilChanged
        disableFileChangeForExisting
        attachments={attachments}
        setAttachments={setAttachments}
        isUploading={isUploading}
        isModel={true}
        showCustomerSelect
        customerOptions={customerOptionsWithCurrent}
        customerRequired
        isLoadingCustomers={isLoadingCustomers}
      />
    </Container>
  );
};

export default ProjectDetail;
